import { inject } from '@angular/core';
import {
  RedirectCommand,
  Router,
  withNavigationErrorHandler,
  type NavigationError,
} from '@angular/router';

/** Angular `RuntimeErrorCode.NO_MATCH` — 라우트 테이블에 없는 URL (`NG04002`). */
const NO_MATCH_ERROR_CODE = 4002;

function isNoMatchError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return code === NO_MATCH_ERROR_CODE || (typeof message === 'string' && message.startsWith('NG04002'));
}

/**
 * 라우트 테이블에 없는 URL(`NG04002`)로 진입하면 `target`(기본 `/`)으로 보낸다.
 *
 * 라우트 테이블의 `**` 대신 `provideRouter` 단계에서 한 번에 처리한다 — 가드 그룹·
 * `redirectTo` 구조와 무관하게 `**` 위치를 앱마다 따질 필요가 없다. 청크 로드 실패 등
 * 다른 네비게이션 오류는 건드리지 않고 기존대로 던진다. `target` 자체가 매칭되지
 * 않는 경우 무한 리다이렉트를 막으려고 그 URL에서는 리다이렉트하지 않는다.
 *
 * 모든 SPA 앱의 `provideRouter(...)`에 넣는다(`tools/ci/lint-angular-shell-conventions.mjs`가 강제).
 * 이미 `**` 라우트가 있는 앱은 그 라우트가 먼저 매칭되므로 동작이 바뀌지 않는다.
 */
export function withNoMatchRedirect(target = '/') {
  return withNavigationErrorHandler((navigationError: NavigationError) => {
    if (!isNoMatchError(navigationError.error)) return undefined;
    const router = inject(Router);
    const targetTree = router.parseUrl(target);
    if (router.serializeUrl(router.parseUrl(navigationError.url)) === router.serializeUrl(targetTree)) {
      return undefined;
    }
    return new RedirectCommand(targetTree);
  });
}
